import React from 'react';
import { Home, Music, Headphones, User, MessageCircle, Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const NotFoundPage: React.FC = () => {
  const quickLinks = [
    { name: 'Home', href: '/', icon: <Home className="h-5 w-5" />, description: 'Return to the homepage' },
    { name: 'Music', href: '/music', icon: <Music className="h-5 w-5" />, description: 'Listen to latest releases' },
    { name: 'Mixes', href: '/mixes', icon: <Headphones className="h-5 w-5" />, description: 'Check out DJ mixes' },
    { name: 'About', href: '/about', icon: <User className="h-5 w-5" />, description: 'Learn about DJ Landan Time' },
    { name: 'Connect', href: '/connect', icon: <MessageCircle className="h-5 w-5" />, description: 'Get in touch and follow' },
  ];

  const popularContent = [
    {
      title: 'House Superstar',
      description: 'Latest release available on all platforms',
      link: 'https://ffm.to/8q55aka',
      image: 'https://ik.imagekit.io/bckqiai0e/House%20Superstar%20-DJ%20Landan%20Time%20-%203000.png'
    },
    {
      title: 'Q2 House Mix 2025',
      description: 'Latest mix on SoundCloud',
      link: 'https://soundcloud.com/djlandantime/q2-house-mix-2025',
      image: 'https://ik.imagekit.io/bckqiai0e/1.png?updatedAt=1747750374575'
    },
    {
      title: 'Spotify Playlist',
      description: 'Curated house music collection',
      link: 'https://open.spotify.com/playlist/4AqzWYRRNMj8bGcNcLVRRt',
      image: 'https://ik.imagekit.io/bckqiai0e/dj-landan-profile.png'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('https://ik.imagekit.io/bckqiai0e/background-waves-2.png')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/20 to-black"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* 404 Header */}
          <div className="mb-12">
            <div className="flex items-center justify-center mb-6">
              <Search className="h-16 w-16 text-purple-500 mr-4" />
              <h1 className="text-8xl font-bold text-purple-500">404</h1>
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Page Not Found</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Looks like this track got lost in the mix! Don't worry, we'll help you find what you're looking for.
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-white mb-8">Quick Navigation</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="bg-gray-900/50 border border-purple-900/30 hover:border-purple-500/50 p-6 rounded-xl transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 flex items-center justify-center text-purple-400 group-hover:text-purple-300 transition-colors duration-300 mb-3">
                      {link.icon}
                    </div>
                    <h4 className="text-white font-semibold mb-2">{link.name}</h4>
                    <p className="text-gray-400 text-sm">{link.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Popular Content */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-8">Popular Content</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {popularContent.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900/50 border border-purple-900/30 hover:border-purple-500/50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="aspect-square relative">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                  </div>
                  <div className="p-6">
                    <h4 className="text-white font-semibold mb-2 group-hover:text-purple-300 transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Back Button */}
          <div className="flex justify-center">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-purple-500/30 transition-all duration-300"
              aria-label="Go back to previous page"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Go Back
            </button>
          </div>

          {/* Contact Info */}
          <div className="mt-16 bg-gray-900/30 border border-purple-900/30 rounded-xl p-8">
            <h3 className="text-xl font-bold text-white mb-4">Still Can't Find What You're Looking For?</h3>
            <p className="text-gray-400 mb-6">
              Get in touch and I'll help you find exactly what you need.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="mailto:dj@djlandantime.co.uk"
                className="bg-transparent hover:bg-purple-600/20 text-purple-400 hover:text-purple-300 border border-purple-600 font-medium py-2 px-6 rounded-full transition-all duration-300"
              >
                Email Me
              </a>
              <a
                href="https://ffm.bio/8y5padb"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-transparent hover:bg-purple-600/20 text-purple-400 hover:text-purple-300 border border-purple-600 font-medium py-2 px-6 rounded-full transition-all duration-300"
              >
                All My Links
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;